import type { TFunction } from 'i18next';
import moment from 'moment';

import type {
	CycleEmployee,
	CycleManager,
	CycleManagerMoment,
	CycleManagerMomentFeedback,
	CycleManagerMomentGoals,
	CycleManagerMomentMessage,
	CycleMoment,
} from '@treams/treams-analytics';
import { CycleTemplateInterval, MomentType } from '@treams/treams-network';

// Column specs shared by the cycle analytics grids. Specs are render-agnostic:
// the grids attach cell renderers and layout. The manager Excel download reuses
// the employee, manager and department columns and adds one text column per
// status step of the moment (for example "feedback requested").

export interface Fraction {
	value: number;
	total: number;
}

export type CycleColumn<Row> =
	| {
			id: string;
			header: string;
			type: 'text';
			value: (row: Row) => string;
			// Optional value used for sorting and filtering when the visible text
			// does not sort naturally (dates, day counts) or carries extra text
			// that should not take part in sorting (the "N users" suffix).
			sortValue?: (row: Row) => number | string | null;
	  }
	| {
			id: string;
			header: string;
			type: 'fraction';
			value: (row: Row) => Fraction | null;
			// Shown when `value` returns null (e.g. a moment that hasn't started).
			emptyText?: string;
	  };

type Localize = <T extends { languageID: string }>(items?: T[] | null, defaultValue?: T) => T;

// ---------------------------------------------------------------------------
// Managers
// ---------------------------------------------------------------------------

export type ManagerProgress = CycleManagerMoment['progress'];

export interface ManagerRow {
	user: CycleManager['user'];
	managed: CycleManager['managed'];
	progress: ManagerProgress;
	goals?: CycleManagerMomentGoals;
	feedback?: CycleManagerMomentFeedback;
	message?: CycleManagerMomentMessage;
}

// Progress steps that can appear as columns for moment types without a
// dedicated column set. Only keys present on the first row are shown.
const PROGRESS_KEYS = [
	'started',
	'employeeShared',
	'managerShared',
	'employeeConfirmed',
	'managerConfirmed',
	'completed',
] as const;

// A manager is "done" for a moment when every progress step has reached its
// total (100%). The relevant steps differ per moment type, so we check all
// values rather than a fixed set of keys.
export function isProgressComplete(progress: ManagerProgress): boolean {
	const { total, ...steps } = progress;

	if (total === 0) {
		return true;
	}

	return Object.values(steps).every((value) => value === undefined || value >= total);
}

export function managerName(user: { firstName: string; lastName: string }): string {
	return `${user.firstName} ${user.lastName}`.trim();
}

// The analytics snapshot doesn't carry a group's archived state, so managed
// departments are filtered against the currently-active group ids.
export function buildManagerRows(
	managers: CycleManager[],
	momentID: string | undefined,
	activeGroupIDs: Set<string>,
): ManagerRow[] {
	if (!momentID) {
		return [];
	}

	return managers
		.map((manager): ManagerRow | null => {
			const cycleManagerMoment = manager.moments.find((moment) => moment.id === momentID);

			if (!cycleManagerMoment) {
				return null;
			}

			return {
				user: manager.user,
				managed: {
					...manager.managed,
					groups: manager.managed.groups.filter((group) => activeGroupIDs.has(group.id)),
				},
				progress: cycleManagerMoment.progress,
				goals: cycleManagerMoment.goals,
				feedback: cycleManagerMoment.feedback,
				message: cycleManagerMoment.message,
			};
		})
		.filter((row): row is ManagerRow => row !== null);
}

export function formatDepartmentNames(row: ManagerRow): string {
	return row.managed.groups.map((group) => group.name).join(', ');
}

export function formatDepartments(row: ManagerRow, t: TFunction): string {
	const groups = formatDepartmentNames(row);

	if (row.managed.groups.length === 0) {
		return groups;
	}

	return `${groups}, ${t('analytics.cycles.managers.users', { users: row.managed.users.length })}`;
}

export function buildManagerColumns({
	rowData,
	momentType,
	t,
}: {
	rowData: ManagerRow[];
	momentType: MomentType | undefined;
	t: TFunction;
}): CycleColumn<ManagerRow>[] {
	const progress = rowData[0]?.progress;

	const goalColumns: CycleColumn<ManagerRow>[] = [
		{
			id: 'goalsCreated',
			header: t('analytics.cycles.managers.table.goalsCreated'),
			type: 'fraction',
			value: (row) => ({ value: row.goals?.created ?? 0, total: row.progress.total }),
		},
		{
			id: 'goalsStarted',
			header: t('analytics.cycles.managers.table.goalsStarted'),
			type: 'fraction',
			value: (row) => ({ value: row.goals?.started ?? 0, total: row.goals?.created ?? 0 }),
		},
		{
			id: 'goalsCompleted',
			header: t('analytics.cycles.managers.table.goalsCompleted'),
			type: 'fraction',
			value: (row) => ({ value: row.goals?.completed ?? 0, total: row.goals?.created ?? 0 }),
		},
	];

	// Feedback moments report the number of feedback requests created from the
	// moment and how many invited givers actually gave feedback.
	const feedbackColumns: CycleColumn<ManagerRow>[] = [
		{
			id: 'feedbackRequested',
			header: t('analytics.cycles.managers.table.feedbackRequested'),
			type: 'fraction',
			value: (row) => ({ value: row.feedback?.requested ?? 0, total: row.progress.total }),
		},
		{
			id: 'feedbackGivers',
			header: t('analytics.cycles.managers.table.feedbackGivers'),
			type: 'fraction',
			value: (row) => ({
				value: row.feedback?.giversCompleted ?? 0,
				total: row.feedback?.givers ?? 0,
			}),
		},
	];

	// Message moments report how many messages were sent (their start date has
	// passed) and how many of those the employee actually read (COMPLETED).
	const messageColumns: CycleColumn<ManagerRow>[] = [
		{
			id: 'messageSent',
			header: t('analytics.cycles.managers.table.messageSent'),
			type: 'fraction',
			value: (row) => ({ value: row.message?.sent ?? 0, total: row.progress.total }),
		},
		{
			id: 'messageRead',
			header: t('analytics.cycles.managers.table.messageRead'),
			type: 'fraction',
			value: (row) => ({ value: row.message?.read ?? 0, total: row.progress.total }),
		},
	];

	const progressColumns: CycleColumn<ManagerRow>[] = progress
		? PROGRESS_KEYS.filter((key) => progress[key] !== undefined).map((key) => ({
				id: key,
				header: t(`analytics.cycles.managers.table.${key}`),
				type: 'fraction',
				value: (row) => ({ value: row.progress[key] ?? 0, total: row.progress.total }),
			}))
		: [];

	const dataColumns =
		momentType === MomentType.Goals
			? goalColumns
			: momentType === MomentType.Feedback
				? feedbackColumns
				: momentType === MomentType.Message
					? messageColumns
					: progressColumns;

	return [
		{
			id: 'manager',
			header: t('analytics.cycles.managers.table.manager'),
			type: 'text',
			value: (row) => managerName(row.user),
		},
		...dataColumns,
		{
			id: 'departments',
			header: t('analytics.cycles.managers.table.departments'),
			type: 'text',
			value: (row) => formatDepartments(row, t),
			// Sort/filter on the department names only, not the "N users" suffix.
			sortValue: (row) => formatDepartmentNames(row),
		},
	];
}

// ---------------------------------------------------------------------------
// Employees
// ---------------------------------------------------------------------------

export interface EmployeeRow {
	user: CycleEmployee['user'];
	managers: CycleEmployee['managers'];
	departments: CycleEmployee['departments'];
	status: string;
	statuses: string[];
	done: boolean;
}

export function buildEmployeeRows(
	employees: CycleEmployee[],
	momentID: string | undefined,
	activeGroupIDs: Set<string>,
): EmployeeRow[] {
	if (!momentID) {
		return [];
	}

	return employees.map((employee) => {
		const moment = employee.moments.find((item) => item.id === momentID);

		return {
			user: employee.user,
			managers: employee.managers,
			departments: employee.departments.filter((department) => activeGroupIDs.has(department.id)),
			status: moment?.status ?? 'notStarted',
			statuses: moment?.statuses ?? [],
			done: moment?.done ?? false,
		};
	});
}

export function formatPersonNames(people: { firstName: string; lastName: string }[]): string {
	return people
		.map((person) => managerName(person))
		.filter((name) => name.length > 0)
		.sort((a, b) => a.localeCompare(b))
		.join(', ');
}

export function formatEmployeeDepartments(row: EmployeeRow): string {
	return row.departments
		.map((department) => department.name)
		.filter((name) => name.length > 0)
		.sort((a, b) => a.localeCompare(b))
		.join(', ');
}

// Least-to-most advanced status steps for a moment, used when no manager row
// carries the statuses (an empty manager list). `notStarted` is the absence of
// these steps, so it is not a column.
const STATUS_KEYS_BY_TYPE: Record<MomentType, string[]> = {
	[MomentType.Goals]: ['goalsCreated', 'goalsStarted', 'goalsCompleted'],
	[MomentType.Feedback]: ['feedbackRequested', 'feedbackGivers'],
	[MomentType.Message]: ['messageSent', 'messageRead'],
	[MomentType.Evaluation]: [
		'started',
		'employeeShared',
		'managerShared',
		'employeeConfirmed',
		'managerConfirmed',
	],
	[MomentType.TalentReview]: ['started', 'completed'],
};

export function statusKeysForMoment(
	managers: CycleManager[],
	momentID: string,
	momentType: MomentType,
): string[] {
	const keys: string[] = [];
	const seen = new Set<string>();

	for (const manager of managers) {
		const moment = manager.moments.find((item) => item.id === momentID);

		if (!moment) {
			continue;
		}

		for (const status of moment.statuses) {
			if (status.key === 'notStarted' || seen.has(status.key)) {
				continue;
			}

			seen.add(status.key);
			keys.push(status.key);
		}
	}

	if (keys.length > 0) {
		return keys;
	}

	return STATUS_KEYS_BY_TYPE[momentType] ?? ['started', 'completed'];
}

export function buildManagerExcelColumns(
	statusKeys: string[],
	t: TFunction,
): CycleColumn<EmployeeRow>[] {
	const shared = new Map(buildEmployeeColumns(t).map((column) => [column.id, column]));
	const pick = (id: string): CycleColumn<EmployeeRow> => {
		const column = shared.get(id);

		if (!column) {
			throw new Error(`Missing cycle column ${id}`);
		}

		return column;
	};

	const statusColumns: CycleColumn<EmployeeRow>[] = statusKeys.map((key) => ({
		id: key,
		header: t(`analytics.cycles.managers.table.${key}`),
		type: 'text',
		value: (row) =>
			row.statuses.includes(key)
				? t('analytics.cycles.managers.statuses.done')
				: t('analytics.cycles.managers.statuses.notDone'),
	}));

	return [pick('employee'), ...statusColumns, pick('manager'), pick('departments')];
}

export function buildEmployeeColumns(t: TFunction): CycleColumn<EmployeeRow>[] {
	return [
		{
			id: 'employee',
			header: t('analytics.cycles.managers.statuses.employee'),
			type: 'text',
			value: (row) => managerName(row.user) || row.user.id,
		},
		{
			id: 'status',
			header: t('analytics.cycles.managers.statuses.status'),
			type: 'text',
			value: (row) => t(`analytics.cycles.managers.table.${row.status}`),
		},
		{
			id: 'manager',
			header: t('analytics.cycles.managers.table.manager'),
			type: 'text',
			value: (row) => formatPersonNames(row.managers),
		},
		{
			id: 'departments',
			header: t('analytics.cycles.managers.table.departments'),
			type: 'text',
			value: (row) => formatEmployeeDepartments(row),
		},
	];
}

// ---------------------------------------------------------------------------
// Moments
// ---------------------------------------------------------------------------

export function formatMomentRemaining(cycleMoment: CycleMoment, t: TFunction): string {
	const hasStarted = moment(cycleMoment.startDate).isBefore(moment());

	if (!hasStarted) {
		return t('analytics.cycles.moments.notStarted');
	}

	const daysRemaining = moment(cycleMoment.endDate).diff(moment(), 'days');

	if (daysRemaining > 0) {
		return t('analytics.cycles.moments.daysRemaining', { days: daysRemaining });
	}

	if (daysRemaining === 0) {
		return t('analytics.cycles.moments.today');
	}

	return t('analytics.cycles.moments.overdue');
}

export function buildMomentColumns({
	interval,
	t,
	localize,
}: {
	interval: CycleTemplateInterval | undefined;
	t: TFunction;
	localize: Localize;
}): CycleColumn<CycleMoment>[] {
	const notStarted = t('analytics.cycles.moments.notStarted');

	const momentColumn: CycleColumn<CycleMoment> = {
		id: 'moment',
		header: t('analytics.cycles.moments.table.moment'),
		type: 'text',
		value: (row) => localize(row.title).text ?? '',
	};

	if (interval === CycleTemplateInterval.Year) {
		return [
			momentColumn,
			{
				id: 'startDate',
				header: t('analytics.cycles.moments.table.startDate'),
				type: 'text',
				value: (row) => (row.startDate ? moment(row.startDate).format('LL') : '-'),
				sortValue: (row) => (row.startDate ? new Date(row.startDate).getTime() : null),
			},
			{
				id: 'endDate',
				header: t('analytics.cycles.moments.table.endDate'),
				type: 'text',
				value: (row) => (row.endDate ? moment(row.endDate).format('LL') : '-'),
				sortValue: (row) => (row.endDate ? new Date(row.endDate).getTime() : null),
			},
			{
				id: 'remaining',
				header: t('analytics.cycles.moments.table.remaining'),
				type: 'text',
				value: (row) => formatMomentRemaining(row, t),
				sortValue: (row) => (row.endDate ? moment(row.endDate).diff(moment(), 'days') : null),
			},
			{
				id: 'employeeStarted',
				header: t('analytics.cycles.moments.table.employeeStarted'),
				type: 'fraction',
				value: (row) =>
					row.progress ? { value: row.progress.started, total: row.progress.total } : null,
				emptyText: notStarted,
			},
			{
				id: 'employeeCompleted',
				header: t('analytics.cycles.moments.table.employeeCompleted'),
				type: 'fraction',
				value: (row) =>
					row.progress ? { value: row.progress.completed, total: row.progress.total } : null,
				emptyText: notStarted,
			},
		];
	}

	return [
		momentColumn,
		{
			id: 'duration',
			header: t('analytics.cycles.moments.table.duration'),
			type: 'text',
			value: (row) => t('analytics.cycles.moments.duration', { days: row.duration }),
			sortValue: (row) => row.duration ?? null,
		},
		{
			id: 'participation',
			header: t('analytics.cycles.moments.table.participation'),
			type: 'fraction',
			value: (row) =>
				row.progress ? { value: row.progress.completed, total: row.progress.total } : null,
			emptyText: notStarted,
		},
	];
}
